import { Outlet, useMatches } from "react-router-dom";
import PausedClusterQueuesNotice from "app/components/shared/PausedClusterQueuesNotice";
import BuildContentNavigation from "../components/BuildContentNavigation";
import { BuildView } from "..";
import { useBuild } from "app/components/Playground/BuildContext";
import Header from "app/components/build/Header";
import Sidebar from "../components/Sidebar";
import { PipelineInfo } from "../../Header/PipelineInfo";
import { Branch } from "../../Header/Branch";
import { Commit } from "../../Header/Commit";
import BuildDuration from "../../Duration";
import PipelineStateIcon, {
  VARIANTS,
} from "app/components/shared/PipelineStateIcon/PipelineStateIcon";
import { twMerge } from "tailwind-merge";
import { StatusBar } from "../components/StatusBar";
import { getBuildColor } from "../lib/getBuildColor";
import { BuildActions } from "../components/BuildActions";
import UserAvatar from "app/components/shared/UserAvatar";
import { Trigger } from "../../Header/Trigger";
import BuildMessage from "../../Message";
import BuildNumber from "../../Header/BuildNumber";

/**
 * Build page layout.
 */
export default function BuildShowLayout() {
  const matches = useMatches();
  const { build, store } = useBuild();

  if (!build || !store) {
    throw new Error("Missing build context");
  }

  const view = [...matches].pop()?.id as BuildView;
  const theme = getBuildColor(build);

  return (
    <>
      <PausedClusterQueuesNotice
        pausedQueues={build.dispatchPausedClusterQueues}
      />

      {Features.BuildSidebar ? (
        <div className="flex flex-auto gap-3">
          <div className="w-80 flex flex-col gap-2">
            <PipelineInfo />

            <div className="relative h-full">
              <div className="absolute -left-2 -right-2 px-2 h-full overflow-y-auto no-scrollbar">
                <Sidebar />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 flex-auto">
            <div className="flex justify-between z-20">
              <div className="flex gap-2 self-end">
                <div className="flex gap-4">
                  <BuildNumber {...build} currentView={view} />

                  {build.branchName && (
                    <Branch className="shrink-0 text-xs" {...build} />
                  )}
                  {build.commitId && <Commit className="text-xs" {...build} />}
                </div>
              </div>

              <div className="shrink-0 flex gap-1 items-center ml-auto">
                <BuildActions />
              </div>
            </div>
            <div
              className="flex flex-col flex-auto rounded-md border border-t-0"
              style={{
                borderColor: theme.primaryColor,
              }}
            >
              <div className="z-10">
                <div className="rounded-t-md overflow-hidden">
                  <StatusBar />
                </div>

                <div
                  className="flex flex-col px-4 pt-3 pb-3 border-b"
                  style={{
                    borderColor: theme.secondaryColor,
                    backgroundColor: theme.tertiaryColor,
                  }}
                >
                  <div className="flex flex-col flex-auto">
                    <div className="flex flex-auto">
                      <div className="flex-auto">
                        <h3
                          className={twMerge(
                            "max-w-prose text-base m-0",
                            build.message && "truncate",
                          )}
                        >
                          <BuildMessage
                            className="text-inherit focus:text-inherit hover:text-inherit"
                            url={build.commitUrl}
                            message={build.message}
                          />
                        </h3>
                      </div>

                      <div className="flex flex-col shrink-0 self-stretch gap-2 pl-4">
                        <div className="flex gap-2 items-center">
                          <div className="rounded-md py-1">
                            <BuildDuration build={build} />
                          </div>

                          <PipelineStateIcon
                            build={build}
                            style={{
                              width: "24px",
                              height: "24px",
                              color: theme.primaryColor,
                            }}
                            variant={VARIANTS.large}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-x-1 gap-y-1 items-center flex-wrap text-xs">
                      <div className="flex gap-1 items-center shrink-0">
                        <UserAvatar
                          size="REGULAR"
                          user={{
                            name: build.authorName,
                            avatar: { url: build.authorAvatar || "" },
                          }}
                        />
                        <div>
                          {build.authorName}
                          {build.creatorVerified && (
                            <div className="build-verified">Verified</div>
                          )}
                        </div>
                      </div>

                      <div className="text-xs">
                        <Trigger {...build} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between px-4 border-b border-gray-400 shadow-sm">
                  <BuildContentNavigation currentView={view} />
                </div>
              </div>

              <div className="flex-auto relative">
                <div className="flex w-full h-full absolute overflow-y-auto">
                  <Outlet />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <Header
            build={build}
            showRebuild={true}
            store={store}
            currentView={view}
            showJobs={view === BuildView.JobList}
          />
          <BuildContentNavigation currentView={view} />
          <Outlet />
        </>
      )}
    </>
  );
}
